import { Component, effect, inject, input, output, signal, untracked } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { KeyValueTypeModel } from '@models/common';
import { OperatorType } from '@models/helpers/operatorType';
import { OperatorTypeDisplayIdEnum, OperatorTypeIdEnum } from '@models/helpers/operatorTypeIdEnum';
import { ScreenConditionModel } from '@models/module/workflowConditionalTransition';
import { ConditionControlGroup } from '@module-definition/complex-conditions/condition-control-group';
import { CipoCheckboxComponent } from '@shared/components/fields/cipo-checkbox/cipo-checkbox.component';
import { CipoNumberComponent } from '@shared/components/fields/cipo-number/cipo-number.component';
import { CipoSelectComponent } from '@shared/components/fields/cipo-select/cipo-select.component';
import { CipoTextComponent } from '@shared/components/fields/cipo-text/cipo-text.component';
import {
  CipoCheckboxControl,
  CipoListControl,
  CipoNumberControl,
  CipoTextControl,
} from '@shared/components/fields/common';
import { ConfirmationDialog } from '@shared/dialogs/confirmation-dialog.component';
import { CipoDialogModule } from '@shared/modules/cipo-dialog.module';
@Component({
  selector: 'cipo-start-screen-condition',
  templateUrl: './start-screen-condition.component.html',
  styleUrls: ['./start-screen-condition.component.scss'],
  imports: [CipoDialogModule, CipoSelectComponent, CipoTextComponent, CipoNumberComponent, CipoCheckboxComponent],
})
export class ScreenConditionComponent {
  dialog = inject(MatDialog);

  screenConditions = input<ScreenConditionModel[]>([]);
  operators = input<KeyValueTypeModel<number>[]>([]);

  conditionsChanged = output<ScreenConditionModel[]>();
  screenConditionsDirty = output<boolean>();
  screenConditionsAllChanged = output<ScreenConditionModel[]>();

  allConditions = signal<ScreenConditionModel[]>([]);
  controls = signal<ConditionControlGroup[]>([]);

  operatorMap = signal<Record<number, KeyValueTypeModel<number>>>({});

  OperatorTypeEnum = OperatorType;
  OperatorTypeIdEnum = OperatorTypeIdEnum;
  OperatorTypeDisplayIdEnum = OperatorTypeDisplayIdEnum;

  constructor() {
    effect(() => {
      const input = this.screenConditions();
      this.allConditions.set([...input]);
      this.controls.set(untracked(() => input.map(cond => this.createStartScreenConditionControl(cond))));
    });
    effect(() => {
      this.operatorMap.set(
        this.operators().reduce(
          (map, op) => {
            map[op.key] = op;
            return map;
          },
          {} as Record<number, KeyValueTypeModel<number>>,
        ),
      );
    });
  }

  deleteStartScreenCondition(index: number) {
    this.dialog
      .open(ConfirmationDialog, {
        data: {
          title: 'complexConditions.general.deleteTitleCondition',
          description: 'complexConditions.general.deleteMessageFieldCondition',
        },
      })
      .afterClosed()
      .subscribe(result => {
        if (!!result) {
          const updated = this.allConditions().filter((_, i) => i !== index);
          this.allConditions.set(updated);
          this.controls.set(updated.map(cond => this.createStartScreenConditionControl(cond)));
          this.screenConditionsDirty.emit(true);
          this.screenConditionsAllChanged.emit(updated);
        }
      });
  }

  updateConditions(newConditions: ScreenConditionModel[]) {
    this.controls.set(newConditions.map(cond => this.createStartScreenConditionControl(cond)));
    this.emitChanges(newConditions);
  }

  createStartScreenConditionControl(cond: ScreenConditionModel): ConditionControlGroup {
    const selectOptions = [
      { key: 'val1', value: 'Value 1' },
      { key: 'val2', value: 'Value 2' },
    ];
    const operatorControl = new CipoListControl(cond.operatorId ?? '', {
      label: 'complexConditions.general.operator',
      options: this.operators().map(o => ({ key: o.key, value: o.value })),
      multiple: false,
      required: true,
    });

    const controlGroup = {
      operatorControl,
      controlText: new CipoTextControl(cond.value?.toString() ?? '', { label: 'Value', required: true }),
      controlNumber: new CipoNumberControl(Number(cond.value) ?? 0, { label: 'Value', required: true }),
      controlCheckbox: new CipoCheckboxControl(Boolean(cond.value)),
      valueControl: new CipoListControl(
        Array.isArray(cond.value) ? cond.value : cond.value != null && cond.value !== '' ? [cond.value] : [],
        {
          label: 'complexConditions.general.value',
          options: selectOptions,
          multiple: true,
          required: true,
        },
      ),
      selectedOperatorId: cond.operatorId ?? null,
    };

    operatorControl.valueChanges.subscribe(newOperatorId => {
      controlGroup.selectedOperatorId = newOperatorId;
      this.onOperatorChanged();
      this.screenConditionsDirty.emit(true);
    });
    controlGroup.controlText.valueChanges?.subscribe(() => {
      this.emitCurrentState();
      this.screenConditionsDirty.emit(true);
    });
    controlGroup.controlNumber.valueChanges?.subscribe(() => {
      this.emitCurrentState();
      this.screenConditionsDirty.emit(true);
    });
    controlGroup.controlCheckbox.valueChanges?.subscribe(() => {
      this.emitCurrentState();
      this.screenConditionsDirty.emit(true);
    });
    controlGroup.valueControl.valueChanges?.subscribe(() => {
      this.emitCurrentState();
      this.screenConditionsDirty.emit(true);
    });
    return controlGroup;
  }

  onOperatorChanged() {
    const models = this.getCurrentConditions();
    this.controls.set(models.map(cond => this.createStartScreenConditionControl(cond)));
    this.emitChanges(models);
  }

  emitCurrentState() {
    const models = this.getCurrentConditions();
    this.allConditions.set(models);
    this.emitChanges(models);
  }

  getCurrentConditions(): ScreenConditionModel[] {
    return this.controls().map(group => {
      const operatorId = Number(group.selectedOperatorId ?? group.operatorControl?.value);
      const value = this.getValueForOperator(group, operatorId);
      const value2 = operatorId === OperatorType.Between ? Number(group.secondControlNumber.value) : undefined;
      return {
        screenId: 0,
        operatorId,
        value,
        value2,
      };
    });
  }

  private emitChanges(models: ScreenConditionModel[]) {
    this.screenConditionsAllChanged.emit(models);
    const valid = models.filter(cond => this.isConditionValid(cond));
    this.conditionsChanged.emit(valid);
  }

  private getValueForOperator(group: any, opId: number): any {
    switch (opId) {
      case OperatorType.Contains:
      case OperatorType.StartsWith:
      case OperatorType.EndsWith: {
        const text = group.controlText.value?.trim();
        return text ? text : null;
      }
      case OperatorType.Equals:
      case OperatorType.DoesNotEqual:
      case OperatorType.LessThen:
      case OperatorType.GraterThen:
      case OperatorType.LessThenOrEqual:
      case OperatorType.GreaterThenOrEqual:
      case OperatorType.Between: {
        const num = Number(group.controlNumber.value);
        return Number.isNaN(num) ? null : num;
      }
      case OperatorType.True:
      case OperatorType.False:
        return !!group.controlCheckbox.value;
      default:
        return group.valueControl.value ?? null;
    }
  }

  private isConditionValid(cond: ScreenConditionModel): boolean {
    if (!cond.operatorId) return false;

    if (cond.operatorId === OperatorType.IsEmpty || cond.operatorId === OperatorType.IsNotEmpty) {
      return true;
    }
    if (cond.operatorId === OperatorType.Between) {
      const v1 = Number(cond.value);
      const v2 = Number((cond as any).value2 ?? null);
      return v1 !== null && v2 !== null && !Number.isNaN(v1) && !Number.isNaN(v2) && v2 > v1;
    }
    const op = this.operators().find(op => op.key === cond.operatorId);
    if (op && op.typeId === 1 && op.displayTypeId === 0) {
      return typeof cond.value === 'string' && cond.value.trim() !== '';
    }
    if (op && op.typeId === 3 && op.displayTypeId === 0) {
      return typeof cond.value === 'boolean';
    }
    if (op && op.typeId === 1 && op.displayTypeId === 3) {
      return Array.isArray(cond.value) && cond.value.length > 0;
    }
    if (op && op.typeId === 2 && op.displayTypeId === 0) {
      return cond.value !== null && cond.value !== '' && !Number.isNaN(Number(cond.value));
    }
    return false;
  }
}
